import { motion } from "framer-motion";
import { useLanguage } from "../contexts/LanguageContext";
import { Check } from "lucide-react";

export default function Pricing() {
  const { t } = useLanguage();
  const tiers = t("pricing.tiers");

  const scrollTo = () => {
    const el = document.querySelector("#contact");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="pricing" data-testid="pricing-section" className="relative py-24 md:py-32 lg:py-48">
      <div className="absolute inset-0 opacity-30">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/edcc2150-0b82-47f9-839c-552e0da40dc2/images/0eed0a22b44b66b666e4d87d25d9f27a6ca47ce352f824b4bfa88adc852e4cb2.png"
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#050505]/90" />
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16 md:mb-20"
        >
          <span className="font-mono text-xs tracking-[0.25em] text-[#00F0FF] mb-4 block">
            {t("pricing.overline")}
          </span>
          <h2 className="font-outfit font-bold text-4xl md:text-5xl leading-tight tracking-tighter text-white mb-4">
            {t("pricing.title")}
          </h2>
          <p className="text-lg text-neutral-400 font-manrope max-w-2xl mx-auto">
            {t("pricing.subtitle")}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {Array.isArray(tiers) && tiers.map((tier, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15, duration: 0.6 }}
              data-testid={`pricing-tier-${tier.name?.toLowerCase()}`}
              className={`relative p-8 md:p-10 rounded-sm border transition-all duration-500 ${
                tier.popular
                  ? "border-[#00F0FF]/50 bg-[#050505] shadow-[0_0_30px_rgba(0,240,255,0.1)] scale-[1.02] md:scale-105"
                  : "border-white/10 bg-[#0a0a0a]/80"
              }`}
            >
              {tier.popular && (
                <div className="absolute -top-px left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00F0FF] to-transparent" />
              )}

              <div className="font-mono text-xs tracking-[0.2em] text-neutral-500 mb-3">
                {tier.popular ? "MOST POPULAR" : `TIER ${i + 1}`}
              </div>

              <h3 className="font-outfit font-bold text-3xl text-white mb-3">
                {tier.name}
              </h3>

              <p className="text-sm text-neutral-400 font-manrope mb-8 leading-relaxed">
                {tier.desc}
              </p>

              <ul className="space-y-3 mb-10">
                {tier.features?.map((feature, fi) => (
                  <li key={fi} className="flex items-start gap-3 text-sm text-neutral-300 font-manrope">
                    <Check className="w-4 h-4 text-[#00F0FF] mt-0.5 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>

              <button
                data-testid={`pricing-cta-${tier.name?.toLowerCase()}`}
                onClick={scrollTo}
                className={`w-full py-3 rounded-sm font-medium text-sm transition-all duration-300 ${
                  tier.popular
                    ? "bg-[#00F0FF] text-black hover:shadow-[0_0_25px_rgba(0,240,255,0.5)]"
                    : "border border-white/20 text-white hover:border-white/40 hover:bg-white/5"
                }`}
              >
                {tier.cta}
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
